import moment from 'moment';
import { useCallback, useEffect, useRef } from 'react';

import { useUserPrint } from '@app/hooks/useUserPrint';
import type { NRecords } from '@app/types';
import {
  createWSConnection,
  invokeWSMethod,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils/createWSConnection';

import { mapEmployeesRecordsResponse } from '@pages/Calendar/utils';

import type { CalendarEvent, Employee } from '@pages/Calendar/data';

interface SocketResponse {
  isSuccess: boolean;
  jsonData: string;
}

interface JournalSocketData {
  Date: string;
  Records: NRecords.ModelEmployeeRecords[];
}

interface UseCalendarWebSocketProps {
  dateString: string;
  onData(employees: Employee[], events: CalendarEvent[]): void;
}

export const useCalendarWebSocket = ({
  dateString,
  onData,
}: UseCalendarWebSocketProps): void => {
  const { database, activeBranchId } = useUserPrint();
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const connect = useCallback(async () => {
    await createWSConnection(async () => {
      await invokeWSMethod('ListenBranch', database, activeBranchId);

      subscribeToWsEvent('OnJournalRecords', (data: SocketResponse) => {
        if (!data.isSuccess) {
          return;
        }

        const parsed: JournalSocketData = JSON.parse(data.jsonData);
        const isSameDate =
          dateString === moment(parsed.Date).format('YYYY-MM-DD');

        if (!isSameDate) {
          return;
        }

        const { employees, events } = mapEmployeesRecordsResponse(
          parsed.Records ?? []
        );

        onDataRef.current(employees, events);
      });
    });
  }, [activeBranchId, database, dateString]);

  useEffect(() => {
    connect();

    return () => {
      unsubscribeFromWsEvent('OnJournalRecords');
    };
  }, [connect]);
};
